import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { toast } from 'sonner';

type Detail = {
  lead: {
    id: string; company_name: string; fantasy_name: string | null; contact_name: string | null;
    email: string | null; cnpj: string | null; raw_phone: string | null; city: string | null;
    state: string | null; current_carrier: string | null; current_plan: string | null;
    monthly_spend: string | null; line_count: number | null; due_day: number | null;
    in_claro_base: boolean | null; status: string; tags: string[]; enrichment: Record<string, unknown>;
    origin: string; score: number | null;
  };
  lines: { id: string; phone_number: string | null; plan_name: string | null; monthly_spend: string | null }[];
  accounts: { id: string; original_name: string; status: string; error: string | null }[];
  proposals: { id: string; chosen_type: number | null; status: string; updated_at: string }[];
  activities: { id: string; type: string; payload: Record<string, unknown>; created_at: string }[];
};

const STATUS_OPTIONS = [
  'novo', 'em_contato', 'proposta_enviada', 'em_negociacao', 'fechado_ganho', 'fechado_perdido', 'descartado',
] as const;
export const STATUS_LABEL: Record<string, string> = {
  novo: 'Novo', em_contato: 'Em contato', proposta_enviada: 'Proposta enviada',
  em_negociacao: 'Em negociação', fechado_ganho: 'Ganho', fechado_perdido: 'Perdido', descartado: 'Descartado',
};

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [missing, setMissing] = useState(false);
  const [suggestions, setSuggestions] = useState<{ type: string; title: string; summary: string; pricing_logic?: string; estimated_monthly?: number }[] | null>(null);
  const [busySuggest, setBusySuggest] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setD(await api.get<Detail>(`/leads/${id}`));
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function onSuggest() {
    if (!id) return;
    setBusySuggest(true);
    try {
      const r = await api.post<{ suggestions: { type: string; title: string; summary: string; pricing_logic?: string; estimated_monthly?: number }[] }>(`/proposals/${id}/suggest`);
      setSuggestions(r.suggestions);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao sugerir');
    } finally {
      setBusySuggest(false);
    }
  }

  async function onChoose(idx: number) {
    if (!id || !suggestions) return;
    try {
      await api.post(`/proposals/${id}`, { chosenType: idx, suggestions });
      toast.success('Rascunho criado. Edite em Propostas.');
      setSuggestions(null);
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao criar rascunho');
    }
  }

  async function onEnrich() {
    if (!id) return;
    try {
      await api.post('/enrichment/run', { leadIds: [id], priority: 0 });
      toast.success('Enriquecimento na fila (prioridade alta).');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao enfileirar');
    }
  }

  async function onUploadPdf(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    const fd = new FormData();
    fd.append('pdf', file);
    fd.append('leadId', id);
    try {
      const r = await api.multipart<{ status: string; message?: string }>('/accounts', fd);
      toast.success(r.status === 'processed' ? 'Conta PDF processada.' : r.message ?? 'Conta recebida.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha no upload');
    }
  }

  async function onStatus(status: string) {
    if (!id) return;
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success('Status atualizado.');
      await load();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao atualizar');
    }
  }

  if (missing) return <div className="text-sm text-[var(--text-dim)]">Lead não encontrado. <Link to="/painel/leads" className="underline">Voltar</Link></div>;
  if (!d) return <div className="text-sm text-[var(--text-dim)]">Carregando lead...</div>;

  const l = d.lead;
  return (
    <div>
      <Link to="/painel/leads" className="text-xs text-[var(--text-dim)] hover:text-[var(--text)]">← Leads</Link>
      <header className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{l.company_name}</h1>
          <p className="mt-0.5 text-sm text-[var(--text-dim)]">
            {[l.fantasy_name, l.city && l.state ? `${l.city}/${l.state}` : null, l.cnpj].filter(Boolean).join(' · ') || 'Sem detalhes'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onEnrich} className="h-9 rounded-lg border border-[var(--border-strong)] px-3 text-sm font-medium hover:bg-[var(--bg-subtle)]">Enriquecer (IA)</button>
          <select
            value={l.status}
            onChange={(e) => onStatus(e.target.value)}
            className="h-9 rounded-lg border border-[var(--border-strong)] bg-white px-2 text-sm"
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
      </header>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Conta</h2>
          <dl className="mt-3 space-y-2">
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Operadora</dt><dd>{l.current_carrier ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Plano</dt><dd>{l.current_plan ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Gasto/mês</dt><dd className="tabular-nums">{l.monthly_spend ? Number(l.monthly_spend).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Linhas</dt><dd>{l.line_count ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Vencimento</dt><dd>{l.due_day ? `dia ${l.due_day}` : '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Origem</dt><dd>{l.origin}</dd></div>
          </dl>
        </section>
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Contato</h2>
          <dl className="mt-3 space-y-2">
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Responsável</dt><dd>{l.contact_name ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Telefone</dt><dd>{l.raw_phone ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Email</dt><dd className="truncate">{l.email ?? '—'}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-[var(--text-dim)]">Base Claro</dt><dd>{l.in_claro_base ? 'Sim' : l.in_claro_base === false ? 'Não' : '—'}</dd></div>
          </dl>
        </section>
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Propostas</h2>
          {d.proposals.length === 0 ? (
            <p className="mt-3 text-[var(--text-dim)]">Nenhuma proposta ainda. Gere no gerador de propostas (Fase 4).</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {d.proposals.map((p) => (
                <li key={p.id} className="flex justify-between gap-2">
                  <span>{p.chosen_type != null ? `Proposta ${p.chosen_type + 1}` : 'Rascunho'}</span>
                  <span className="text-[var(--text-dim)]">{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <section className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Propostas (IA)</h2>
            <p className="mt-1 text-[var(--text-dim)]">A IA cruza a conta do lead com as ofertas do mês e sugere 3 cenários: migração, renovação ou expansão.</p>
          </div>
          <button onClick={onSuggest} disabled={busySuggest} className="h-9 rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
            {busySuggest ? 'Pensando...' : 'Sugerir 3 propostas (IA)'}
          </button>
        </div>
        {suggestions && suggestions.length > 0 && (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {suggestions.map((s, i) => (
              <div key={s.title} className="rounded-lg border border-[var(--border-strong)] p-3">
                <span className="rounded bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-dim)]">{s.type}</span>
                <p className="mt-2 font-medium">{s.title}</p>
                <p className="mt-1 text-xs text-[var(--text-dim)]">{s.summary}</p>
                {s.estimated_monthly != null && (
                  <p className="mt-2 text-xs tabular-nums">Estimativa: {Number(s.estimated_monthly).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês</p>
                )}
                <button onClick={() => onChoose(i)} className="mt-3 h-8 w-full rounded-md border border-[var(--border-strong)] text-xs font-medium hover:bg-[var(--bg-subtle)]">Usar esta base</button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Ficha (editar manualmente)</h2>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const body: Record<string, unknown> = {};
            for (const k of ['current_carrier', 'current_plan', 'city', 'state', 'raw_phone', 'contact_name', 'email']) {
              const v = String(fd.get(k) ?? '').trim();
              if (v) body[k] = v;
            }
            for (const k of ['monthly_spend', 'line_count', 'due_day']) {
              const v = String(fd.get(k) ?? '').trim();
              if (v) body[k] = Number(v.replace(/\./g, '').replace(',', '.'));
            }
            try {
              await api.patch(`/leads/${id}`, body);
              toast.success('Ficha atualizada.');
              await load();
            } catch (err) {
              toast.error((err as { message?: string }).message ?? 'Falha ao salvar ficha');
            }
          }}
          className="mt-3 grid gap-2 sm:grid-cols-4"
        >
          <input name="current_carrier" placeholder="Operadora atual" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="current_plan" placeholder="Plano atual" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="monthly_spend" placeholder="Gasto/mês (R$)" inputMode="decimal" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="line_count" placeholder="Nº de linhas" inputMode="numeric" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="raw_phone" placeholder="Telefone" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="contact_name" placeholder="Contato" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <input name="email" placeholder="Email" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <div className="gap-2"><input name="city" placeholder="Cidade" className="h-9 w-2/3 mr-[2px] rounded-md border border-[var(--border-strong)] px-2 text-xs no-margin" />
          <input name="state" placeholder="UF" maxLength={2} className="h-9 w-[38px] rounded-md border border-[var(--border-strong)] px-2 text-xs" /></div>
          <input name="due_day" placeholder="Vence dia" inputMode="numeric" className="h-9 rounded-md border border-[var(--border-strong)] px-2 text-xs" />
          <button type="submit" className="h-9 rounded-md bg-[var(--accent)] px-4 text-xs font-semibold text-white hover:bg-[#c40a20]">Salvar ficha</button>
        </form>
      </section>
      <section className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Contas (PDF)</h2>
        {d.accounts.length === 0 ? (
          <p className="mt-2 text-[var(--text-dim)]">Nenhuma conta anexada. Anexe a fatura em PDF para a IA analisar o consumo real.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {d.accounts.map((a) => (
              <li key={a.id} className="flex justify-between gap-2 border-b border-[var(--border)] pb-1 last:border-0">
                <span>{a.original_name}</span>
                <span className="text-[var(--text-dim)]">{a.status}</span>
              </li>
            ))}
          </ul>
        )}
        <label className="mt-3 inline-flex h-9 cursor-pointer items-center rounded-lg border border-[var(--border-strong)] px-3 text-sm font-medium hover:bg-[var(--bg-subtle)]">
          Anexar conta (PDF ou foto/print)
          <input type="file" accept="application/pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onUploadPdf} />
        </label>
      </section>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Linhas telefônicas</h2>
          {d.lines.length === 0 ? (
            <p className="mt-3 text-[var(--text-dim)]">Nenhuma linha registrada.</p>
          ) : (
            <table className="mt-3 w-full">
              <tbody>
                {d.lines.map((ln) => (
                  <tr key={ln.id} className="border-b border-[var(--border)] last:border-0">
                    <td className="py-2 tabular-nums">{ln.phone_number ?? '—'}</td>
                    <td className="py-2 text-[var(--text-dim)]">{ln.plan_name ?? ''}</td>
                    <td className="py-2 text-right tabular-nums">{ln.monthly_spend ? Number(ln.monthly_spend).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <section className="rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-[var(--text-dim)]">Histórico</h2>
          {d.activities.length === 0 ? (
            <p className="mt-3 text-[var(--text-dim)]">Sem eventos ainda.</p>
          ) : (
            <ol className="mt-3 space-y-0">
              {d.activities.map((a) => (
                <li key={a.id} className="border-l-2 border-[var(--border)] py-2 pl-3 text-[13px]">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{String((a.payload as { evento?: string }).evento ?? a.type)}</span>
                    <time className="text-[var(--text-dim)]">{new Date(a.created_at).toLocaleString('pt-BR')}</time>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
